import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useUserOrganizations } from '@/hooks/useUserProfile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, ArrowRight, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: userOrgs = [] } = useUserOrganizations();
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);

  const hasExistingOrgs = userOrgs.length > 0;

  const handleCreateOrganization = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) return;
    
    setIsLoading(true);
    const formData = new FormData(e.currentTarget);
    const orgName = formData.get('orgName') as string;
    const billingEmail = formData.get('billingEmail') as string;

    try {
      const { data, error } = await supabase.rpc('create_organization_with_admin', {
        org_name: orgName,
        org_billing_email: billingEmail || null,
      });

      if (error) throw error;

      toast.success('Organisation créée avec succès !');
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Error creating organization:', error);
      toast.error(error.message || 'Erreur lors de la création');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/50 p-4">
      <div className="w-full max-w-lg animate-slide-up">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl gradient-primary shadow-elegant">
              <Building className="h-7 w-7 text-primary-foreground" />
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold mb-2">
            {hasExistingOrgs ? 'Nouvelle organisation' : 'Bienvenue sur izimmo'}
          </h1>
          <p className="text-muted-foreground">
            {hasExistingOrgs 
              ? 'Créez une nouvelle organisation pour gérer un autre portefeuille'
              : 'Configurez votre espace de travail pour commencer'}
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className={`h-2 w-16 rounded-full transition-colors ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
          <div className={`h-2 w-16 rounded-full transition-colors ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
        </div>

        {step === 1 && (
          <Card className="shadow-elegant">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                <Sparkles className="h-6 w-6 text-primary" />
              </div>
              <CardTitle className="font-display text-xl">Créez votre organisation</CardTitle>
              <CardDescription>
                Une organisation regroupe vos clients, SCI et collaborateurs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateOrganization} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="orgName">Nom de l'organisation *</Label>
                  <Input
                    id="orgName"
                    name="orgName"
                    placeholder="Ex: Cabinet Dupont & Associés"
                    required
                    autoFocus
                  />
                  <p className="text-xs text-muted-foreground">
                    Le nom de votre cabinet comptable ou groupe
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="billingEmail">Email de facturation</Label>
                  <Input
                    id="billingEmail"
                    name="billingEmail"
                    type="email"
                    placeholder="comptabilite@cabinet.fr"
                  />
                </div>

                <div className="pt-4">
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      'Création en cours...'
                    ) : (
                      <>
                        Créer et continuer
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Info */}
        <p className="text-center text-sm text-muted-foreground mt-6">
          Vous serez administrateur de cette organisation et pourrez inviter des collaborateurs.
        </p>

        {/* Back to dashboard link if user has existing organizations */}
        {hasExistingOrgs && (
          <div className="text-center mt-4">
            <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" />
              Retour au tableau de bord
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
